import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { creditsService } from '../services/api';
import SkeletonLoader from '../components/ui/SkeletonLoader';

function CreditsPage() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState('');
    const [buyingPack, setBuyingPack] = useState('');
    const [cancelling, setCancelling] = useState(false);
    const [error, setError] = useState('');
    const [transactions, setTransactions] = useState([]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('success') === 'true') {
            setTimeout(() => fetchData(), 1500);
        } else {
            fetchData();
        }
    }, []);

    const fetchData = async () => {
        try {
            const [plansRes, historyRes] = await Promise.all([
                creditsService.getPackages(),
                creditsService.getHistory(),
            ]);
            setData(plansRes.data);
            setTransactions(historyRes.data.transactions);
        } catch (err) {
            setError(t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubscribe = async (planId) => {
        setSubscribing(planId);
        setError('');
        try {
            const response = await creditsService.subscribe(planId);
            window.location.href = response.data.checkout_url;
        } catch (err) {
            setError(t('common.error'));
            setSubscribing('');
        }
    };

    const handleBuyPack = async (packId) => {
        setBuyingPack(packId);
        setError('');
        try {
            const response = await creditsService.buyPack(packId);
            window.location.href = response.data.checkout_url;
        } catch (err) {
            setError(t('common.error'));
            setBuyingPack('');
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? You will keep access until the end of your billing period.')) return;
        setCancelling(true);
        try {
            await creditsService.cancel();
            await fetchData();
        } catch (err) {
            setError(t('common.error'));
        } finally {
            setCancelling(false);
        }
    };

    const COSTS = data?.costs || {};
    const subscription = data?.subscription || {};
    const params = new URLSearchParams(window.location.search);
    const showSuccess = params.get('success') === 'true';
    const showCancelled = params.get('cancelled') === 'true';

    if (loading) return (
        <Layout>
            <div className="page-container"><SkeletonLoader /></div>
        </Layout>
    );

    return (
        <Layout>
            <div className="page-container">
                <h1>{t('credits.title')}</h1>
                <p className="subtitle">{t('credits.subtitle')}</p>

                {showSuccess && (
                    <div className="success-message" style={{ fontSize: '1rem', padding: '1rem' }}>
                        {t('credits.payment_success')}
                    </div>
                )}
                {showCancelled && (
                    <div className="error-message" style={{ fontSize: '1rem', padding: '1rem' }}>
                        {t('credits.payment_cancelled')}
                    </div>
                )}
                {error && <div className="error-message">{error}</div>}

                {/* Current balance + subscription status */}
                <div className="card credits-balance-card">
                    <div className="credits-balance">
                        <div className="credits-balance-number">{data?.credits ?? 0}</div>
                        <div className="credits-balance-label">{t('credits.remaining')}</div>
                        {subscription.status === 'active' && (
                            <div style={{
                                marginTop: '0.5rem',
                                background: '#dcfce7',
                                color: '#15803d',
                                borderRadius: '20px',
                                padding: '0.2rem 0.75rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                            }}>
                                {subscription.plan?.toUpperCase()} ACTIVE
                            </div>
                        )}
                    </div>
                    <div className="credits-cost-grid">
                        {[
                            { label: t('credits.resume_analysis'), cost: COSTS.resume_analysis },
                            { label: t('credits.cv_tailoring'), cost: COSTS.cv_tailor },
                            { label: t('credits.career_recommendations'), cost: COSTS.career_recommendations },
                            { label: t('credits.learning_roadmap'), cost: COSTS.roadmap },
                            { label: t('credits.interview_session'), cost: COSTS.interview_start },
                            { label: t('credits.answer_evaluation'), cost: COSTS.answer_evaluation },
                        ].map(item => (
                            <div key={item.label} className="credits-cost-item">
                                <span>{item.label}</span>
                                <strong>{item.cost} cr</strong>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Active subscription management */}
                {subscription.status === 'active' && (
                    <div className="card" style={{ marginBottom: '1.5rem', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ color: '#15803d' }}>Active Subscription</h2>
                                <p style={{ color: '#166534' }}>
                                    {subscription.plan?.charAt(0).toUpperCase() + subscription.plan?.slice(1)} Plan —
                                    credits reset monthly
                                </p>
                            </div>
                            <button
                                onClick={handleCancel}
                                disabled={cancelling}
                                className="btn-danger"
                            >
                                {cancelling ? 'Cancelling...' : 'Cancel Plan'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Plans */}
                <h2 style={{ marginBottom: '0.5rem' }}>
                    Buy Once
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                    Start with a small one-time pack, or choose a monthly plan for better value.
                </p>

                <div className="credits-packages" style={{ marginBottom: '2rem' }}>
                    {(data?.packs || []).map(pack => (
                        <div key={pack.id} className="credits-package-card" style={{ border: '2px solid #cbd5e1' }}>
                            <div className="popular-badge" style={{ background: '#0f766e' }}>Best for trial</div>
                            <div className="package-name">{pack.name}</div>
                            <div className="package-credits">{pack.credits}</div>
                            <div className="package-credits-label">credits one time</div>
                            <div className="package-price">${pack.price}</div>
                            <div className="package-per-credit">single purchase</div>
                            <div style={{
                                fontSize: '0.78rem',
                                color: '#64748b',
                                marginTop: '0.5rem',
                                marginBottom: '0.5rem',
                            }}>
                                ${(pack.price / pack.credits).toFixed(3)} per credit
                            </div>
                            <button
                                onClick={() => handleBuyPack(pack.id)}
                                disabled={buyingPack === pack.id}
                                className="btn-primary"
                                style={{ width: '100%', marginTop: '0.5rem' }}
                            >
                                {buyingPack === pack.id ? 'Redirecting...' : `Buy Once — $${pack.price}`}
                            </button>
                        </div>
                    ))}
                </div>

                <h2 style={{ marginBottom: '0.5rem' }}>
                    {subscription.status === 'active' ? 'Change Plan' : t('credits.buy_credits')}
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                    Monthly plans are the best value if you use credits regularly.
                </p>

                <div className="credits-packages">
                    {(data?.plans || []).map(plan => {
                        const isCurrentPlan = subscription.plan === plan.id && subscription.status === 'active';
                        return (
                            <div
                                key={plan.id}
                                className={`credits-package-card ${plan.id === 'pro' ? 'popular' : ''} ${isCurrentPlan ? 'current-plan' : ''}`}
                            >
                                {plan.id === 'pro' && !isCurrentPlan && (
                                    <div className="popular-badge">{t('credits.most_popular')}</div>
                                )}
                                {isCurrentPlan && (
                                    <div className="popular-badge" style={{ background: '#10b981' }}>Current Plan</div>
                                )}
                                <div className="package-name">{plan.name}</div>
                                <div className="package-credits">{plan.credits}</div>
                                <div className="package-credits-label">credits / month</div>
                                <div className="package-price">${plan.price}</div>
                                <div className="package-per-credit">per month</div>
                                <div style={{
                                    fontSize: '0.78rem',
                                    color: '#64748b',
                                    marginTop: '0.5rem',
                                    marginBottom: '0.5rem',
                                }}>
                                    ${(plan.price / plan.credits).toFixed(3)} per credit
                                </div>
                                <button
                                    onClick={() => handleSubscribe(plan.id)}
                                    disabled={subscribing === plan.id || isCurrentPlan}
                                    className={`btn-primary ${plan.id === 'pro' ? 'btn-popular' : ''}`}
                                    style={{ width: '100%', marginTop: '0.5rem' }}
                                >
                                    {isCurrentPlan ? 'Current Plan' :
                                     subscribing === plan.id ? 'Redirecting...' :
                                     `Subscribe — $${plan.price}/mo`}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Transaction history */}
                {transactions.length > 0 && (
                    <div className="card" style={{ marginTop: '2rem' }}>
                        <h2>{t('credits.history')}</h2>
                        <div className="transactions-list">
                            {transactions.map(tr => (
                                <div key={tr.id} className="transaction-item">
                                    <div className="transaction-info">
                                        <span className={`transaction-type ${tr.type}`}>
                                            {tr.type === 'subscription' ? 'Subscription' :
                                            tr.type === 'purchase' ? t('credits.purchase') :
                                             tr.type === 'bonus' ? t('credits.bonus') :
                                             tr.type === 'refund' ? t('credits.refund') :
                                             t('credits.deduction')}
                                        </span>
                                        <span className="transaction-desc">{tr.description}</span>
                                    </div>
                                    <div className="transaction-meta">
                                        <span className={`transaction-credits ${tr.credits > 0 ? 'positive' : 'negative'}`}>
                                            {tr.credits > 0 ? '+' : ''}{tr.credits}
                                        </span>
                                        <span className="transaction-date">
                                            {new Date(tr.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default CreditsPage;